<template>
  <v-modal id="show-email-warning-modal" class-name="modal-fullscreen-sm-down" size="xl" @closed="$emit('closed')">
    <modal-header>
      <modal-title class="d-flex align-items-center gap-8">
        <v-icon icon="info" /> {{ $t("tourPricing.invoice.summary") }}
      </modal-title>
    </modal-header>

    <modal-body>
      <div>
        <v-checkbox
          id="invoice-summary-select-all"
          :for-key="`invoice-summary-select-all`"
          v-model="selectAll"
          label=" "
          @change="toggleAllSuppliers"
        />
      </div>
      <div
        v-for="(type, index) in pricing.items.filter(
          (item: PricingItem) => !item.name.startsWith('null') && !item.name.startsWith('unknown')
        )"
        :key="index"
      >
        <div v-for="(supplier, sIndex) in type.suppliers" :key="sIndex" class="mb-4 border p-8 rounded">
          <div class="d-flex">
            <div class="w-05">
              <v-checkbox
                :id="`invoice-summary-${supplier.id}-${supplier.tourDayItemIds}`"
                v-model="supplierCheckedMap[`${supplier.id}_${supplier.tourDayItemIds}`]"
                label=" "
                :isDisabled="supplier.isConnectedToHolded && supplier.paymentStatus === 'complete'"
                class="text-nowrap supplier-checkbox"
              />
            </div>
            <div class="supllier-data-container">
              <!-- Supplier Header -->
              <div
                class="d-flex justify-content-between align-items-center bg-light p-3 cursor-pointer"
                data-bs-toggle="collapse"
                :data-bs-target="`#collapse-${supplier.id}-${sIndex}`"
                :aria-controls="`collapse-${supplier.id}-${sIndex}`"
                :aria-expanded="isSupplierOpen(supplier.id)"
                @click="toggleSupplier(supplier.id)"
              >
                <div class="d-flex align-items-center gap-16">
                  <v-icon :icon="getAssociatedIcon(type.name)" size="md" />
                  <strong>{{ supplier.name }}</strong>
                  <span v-if="supplier.account === null" class="badge bg-danger fw-normal"> Wise N/A </span>
                  <span v-if="supplier.isConnectedToHolded" class="tooltip-wrapper">
                    <v-button class="btn-success p-1" variant="icon">
                      <v-icon icon="check" size="xs" />
                    </v-button>
                    <span class="tooltip-text">
                      {{ $t("tourPricing.integretedHolded") }}
                    </span>
                  </span>
                  <span class="tooltip-wrapper">
                    <v-button
                      class="p-2"
                      :class="supplier.paymentStatus === 'complete' ? 'btn-success' : 'btn-danger'"
                      variant="icon"
                    >
                      <v-icon icon="euro" size="xs" />
                    </v-button>
                    <span class="tooltip-text">
                      {{ formatText(supplier.paymentStatus) }} | {{ formatText(supplier.paymentMethod) }}
                    </span>
                  </span>
                </div>
                <div class="d-flex align-items-center gap-16">
                  <span class="me-3">{{ supplier.comonNetPrice ?? 0 }} <v-icon icon="euro" size="sm" /></span>
                  <v-icon :icon="isSupplierOpen(supplier.id) ? 'chevron-up' : 'chevron-down'" size="md" />
                </div>
              </div>

              <!-- Accordion Body -->
              <div :id="`collapse-${supplier.id}-${sIndex}`" class="collapse bg-white mt-4">
                <div class="p-3">
                  <div class="row fw-bold border-bottom pb-2 mb-2">
                    <div class="col-3">{{ $t("tourPricing.invoice.name") }}</div>
                    <div class="col-2 text-center">{{ $t("tourPricing.quantity") }}</div>
                    <div class="col-5">{{ $t("tourPricing.invoice.date") }}</div>
                    <div class="col-2">{{ $t("tourPricing.invoice.price") }}</div>
                  </div>

                  <div v-for="(item, i) in supplier.itemResourcesGrouped" :key="i" class="row mt-2 p-4 border-bottom">
                    <div class="col-3">{{ item.name }}</div>
                    <div class="col-2 text-center">{{ item.quantity }}</div>
                    <div class="col-5">
                      <div class="d-flex">
                        <span>{{ getDayName(item.date) }},</span>
                        <span class="ms-4">
                          {{ $str.formatDateTime(item.date, settings.formatDate) }}
                          <template v-if="item.type === 'hotel'">
                            -
                            <template v-if="!!item.endDate">
                              {{ $str.formatDateTime(item.endDate, settings.formatDate) }}
                            </template>
                            <template v-else>
                              {{ $t("general.shared.noEndDate") }}
                            </template>
                          </template>
                        </span>
                      </div>
                    </div>
                    <div class="col-2">{{ item.amountPending }}<v-icon icon="euro" size="sm" /></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="mb-4 border border-2 border-dark p-16 rounded overflow-hidden">
        <div class="d-flex justify-content-between align-items-center bg-light p-3">
          <div class="d-flex align-items-center gap-16">
            <strong>{{ $t("tourPricing.invoice.total") }}</strong>
          </div>
          <div class="d-flex align-items-center gap-16 me-16">
            <span class="me-3">{{ checkedSupplierTotal }}<v-icon icon="euro" size="sm" /></span>
          </div>
        </div>
      </div>
    </modal-body>
    <modal-footer>
      <div class="d-flex gap-8">
        <v-button
          class="btn-sm btn-danger align-self-end cursor-pointer"
          size="sm"
          variant="danger"
          @click="$emit('closed')"
        >
          {{ $t("general.button.close") }}
        </v-button>
        <v-button
          class="btn-sm btn-success align-self-end cursor-pointer"
          size="sm"
          variant="success"
          :disabled="generatePayload.length === 0"
          @click="genrateInvoice"
        >
          {{ $t("general.button.confirm") }}
        </v-button>
      </div>
    </modal-footer>
  </v-modal>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { DateTime } from "luxon";
import { mapState } from "pinia";
import { useAppStore } from "@/stores/app";
import { mapConcepts } from "@/helpers/ConceptHelper";
import { VCheckbox } from "@uniqoders/form";
import useConcepts from "@/helpers/Concepts";
import VIcon from "@/components/vendor/basic/icon/VIcon.vue";
import VModal from "@/components/vendor/basic/modal/VModal.vue";
import ModalHeader from "@/components/vendor/basic/modal/ModalHeader.vue";
import ModalTitle from "@/components/vendor/basic/modal/ModalTitle.vue";
import ModalBody from "@/components/vendor/basic/modal/ModalBody.vue";
import ModalFooter from "@/components/vendor/basic/modal/ModalFooter.vue";
import VButton from "@/components/vendor/basic/button/VButton.vue";
import api from "@/api";

type Product = {
  name: string;
  quantity: number;
  date: string;
  endDate: string;
  amountPending: number;
};

type Supplier = {
  paymentStatus: string;
  isConnectedToHolded: boolean;
  tourDayItemIds: any;
  id: string | number;
  name: string;
  comonNetPrice: number;
  itemResourcesGrouped: Product[];
};

type PricingItem = {
  name: string;
  suppliers: Supplier[];
};

type PayloadProduct = {
  name: string;
  qty: string;
  date: string;
  endDate: string;
  price: number;
};

type SupplierPayload = {
  supplierId: string;
  tourDayItemId: string;
  name: string;
  total: string;
  products: PayloadProduct[];
};

export default defineComponent({
  name: "TheInvoiceSummaryModal",
  components: {
    VModal,
    VButton,
    ModalHeader,
    ModalTitle,
    ModalBody,
    ModalFooter,
    VIcon,
    VCheckbox,
  },
  emits: ["closed", "reloadItems"],
  props: {
    agency: {
      type: String,
      required: true,
    },
    caseId: {
      type: Number,
      required: true,
    },
    tourId: {
      type: Number,
      required: true,
    },
    pricing: {
      type: Object,
      required: true,
    },
    paymentMethod: {
      type: Object,
      required: false,
    },
  },
  setup() {
    return {
      ...useConcepts(),
    };
  },
  data() {
    return {
      openSupplierIds: [] as number[],
      supplierCheckedMap: {} as Record<string, boolean>,
      selectAll: false,
    };
  },
  watch: {
    supplierCheckedMap: {
      handler(newMap) {
        const keys = Object.keys(newMap);
        const allChecked = keys.length > 0 && keys.every((key) => newMap[key]);
        const noneChecked = keys.every((key) => !newMap[key]);

        if (allChecked) {
          this.selectAll = true;
        } else if (noneChecked) {
          this.selectAll = false;
        } else {
          this.selectAll = true;
        }
      },
      deep: true,
      immediate: true,
    },
  },
  computed: {
    ...mapState(useAppStore, ["settings"]),
    itemTypes(): any {
      if (!!this.concepts && this.concepts["supplier.type"]) {
        return mapConcepts(this.concepts["supplier.type"]);
      }

      return [];
    },
    generatePayload(): SupplierPayload[] {
      const payload: SupplierPayload[] = this.pricing.items
        .filter((item: PricingItem) => !item.name.startsWith("null") && !item.name.startsWith("unknown"))
        .reduce((acc: SupplierPayload[], type: PricingItem) => {
          type.suppliers.forEach((supplier: Supplier) => {
            if (
              !this.supplierCheckedMap[`${supplier.id}_${supplier.tourDayItemIds}`] ||
              (supplier.isConnectedToHolded && supplier.paymentStatus === "complete")
            )
              return;

            const supplierPayload: SupplierPayload = {
              supplierId: String(supplier.id),
              tourDayItemId: String(supplier.tourDayItemIds),
              name: supplier.name,
              total: String(supplier.comonNetPrice ?? 0),
              products: supplier.itemResourcesGrouped.map(
                (product: Product): PayloadProduct => ({
                  name: product.name,
                  qty: String(product.quantity),
                  date: product.date,
                  endDate: product.endDate,
                  price: product.amountPending,
                })
              ),
            };

            acc.push(supplierPayload);
          });
          return acc;
        }, []);

      return payload;
    },
    checkedSupplierTotal() {
      let total = 0;
      this.pricing.items.forEach((type: { suppliers: any[] }) => {
        type.suppliers.forEach(
          (supplier: {
            id: any;
            tourDayItemIds: any;
            isConnectedToHolded: any;
            paymentStatus: string;
            comonNetPrice: any;
          }) => {
            const key = `${supplier.id}_${supplier.tourDayItemIds}`;

            const isDisabled =
              this.paymentMethod?.key === "holded"
                ? supplier.isConnectedToHolded
                : supplier.paymentStatus === "complete";

            if (!this.supplierCheckedMap[key] || isDisabled) return;

            total += Number(supplier.comonNetPrice) || 0;
          }
        );
      });
      return total;
    },
  },
  methods: {
    formatText(value: string) {
      if (!value) return "";
      return value
        .toLowerCase() // make all lower
        .split("_") // split on underscores
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1)) // capitalize each part
        .join(" "); // join with spaces
    },
    getDayName(date: string) {
      return DateTime.fromISO(date).toFormat("cccc");
    },
    getAssociatedIcon(type: string) {
      let defaultIcon = "compass";

      if (!!type) {
        defaultIcon = this.itemTypes.find((item: any) => item.key === type)?.icon;
      }

      return defaultIcon;
    },
    toggleSupplier(id: any) {
      const index = this.openSupplierIds.indexOf(id);
      if (index > -1) {
        this.openSupplierIds.splice(index, 1);
      } else {
        this.openSupplierIds.push(id);
      }
    },
    isSupplierOpen(id: any) {
      return this.openSupplierIds.includes(id);
    },
    async genrateInvoice() {
      const payload = this.generatePayload;

      const totalSuppliers = payload.length;
      const zeroPriceSuppliers = payload.filter((supplier: any) => Number(supplier.total) === 0).length;

      let confirmationText = this.$t("tourPricing.confirmInvoiceMessage");

      if (zeroPriceSuppliers > 0) {
        confirmationText = this.$t("tourPricing.confirmInvoiceMessageWithZeroSuppliers", {
          zero: zeroPriceSuppliers,
          total: totalSuppliers,
          message: `${this.$t("tourPricing.confirmInvoiceMessage")}. ${this.$t("tourPricing.confirmMessage")}`,
        });
      }

      await this.$modal.confirm({
        title: this.$t("tourPricing.invoiceDetail"),
        text: confirmationText,
        leftButtonClasses: "btn-tertiary",
        confirmButtonText: this.$t("general.button.confirm"),
        confirmButtonCallback: () => this.confirmInvoiceSummary(),
      });
    },
    async confirmInvoiceSummary() {
      try {
        if (this.paymentMethod?.key === "holded") {
          await api.invoice.invoiceSummary(this.agency, this.caseId, this.tourId, this.generatePayload);
        } else {
          await api.invoice.invoiceSummaryWise(this.agency, this.caseId, this.tourId, this.generatePayload);
        }
        this.$toast.success(this.$t("general.shared.savedChanges"));
        this.$emit("reloadItems");
        this.$emit("closed");
        this.selectAll = false;
        this.supplierCheckedMap = {};
      } catch (error: any) {
        this.$toast.error(error.response.data.message);
      }
    },
    toggleAllSuppliers() {
      this.pricing.items
        .filter((item: PricingItem) => !item.name.startsWith("null") && !item.name.startsWith("unknown"))
        .forEach((type: PricingItem) => {
          type.suppliers.forEach((supplier: Supplier) => {
            this.supplierCheckedMap[`${supplier.id}_${supplier.tourDayItemIds}`] = this.selectAll;
          });
        });
    },
  },
});
</script>

<style scoped>
.supplier-checkbox {
  display: flex;
  justify-content: center;
  padding-top: 3px;
}

.supllier-data-container {
  width: 95%;
}

.tooltip-wrapper {
  position: relative;
  display: inline-block;
}

.tooltip-text {
  visibility: hidden;
  background-color: #bbe5f1;
  color: black;
  text-align: center;
  padding: 6px 12px;
  border-radius: 6px;
  position: absolute;
  z-index: 1000;
  bottom: 115%;
  left: 50%;
  transform: translateX(-50%);
  white-space: nowrap;
  opacity: 0;
  transition: opacity 0.3s;
}

.tooltip-text::after {
  content: "";
  position: absolute;
  top: 100%;
  left: 50%;
  margin-left: -6px;
  border-width: 6px;
  border-style: solid;
  border-color: #bbe5f1 transparent transparent transparent;
}

.tooltip-wrapper:hover .tooltip-text {
  visibility: visible;
  opacity: 1;
}
</style>
